/**
 * Client-side helpers for the email-template editor's live preview.
 *
 * The preview substitutes `{{token}}` placeholders exactly the way the server render does: the
 * six brand globals come from Business Settings, the per-template variables from sample values.
 * Doing it client-side keeps the preview truly live — it reflects unsaved edits in either the
 * drag-drop builder or the raw-HTML editor — while the server `preview`/`test-send` endpoints
 * remain the source of truth for what actually gets sent.
 */
import type { PlatformSettings } from '../contracts/types.js';

/** Plausible sample values for a preview, keyed by variable name. */
export const SAMPLE_VARS: Readonly<Record<string, string>> = {
  firstName: 'Amara',
  code: '481920',
  resetUrl: 'https://example.com/reset',
  amount: '₦25,000.00',
  asset: '0.0042 BTC',
  reference: 'GX-8H2K91',
  fromAmount: '$100.00',
  toAmount: '₦158,400.00',
  bankName: 'GTBank ••34',
  recipient: 'Chidi O.',
  method: 'Bank transfer',
  brand: 'Amazon',
  agentName: 'Tobi',
  message: 'Thanks for reaching out. I have credited your wallet.',
  event: 'New sign-in',
  device: 'iPhone 15 · Lagos, NG',
  time: '30 Jul 2026, 14:22 WAT',
};

/** Build the six brand globals from the current platform settings. */
export function brandGlobals(settings: PlatformSettings, logoUrl: string | null): Record<string, string> {
  return {
    logo: logoUrl ?? 'https://dummyimage.com/160x36/5b2ce6/ffffff.png&text=Logo',
    platformName: settings.platformName,
    primaryColor: settings.primaryColor,
    accentColor: settings.accentColor,
    supportEmail: settings.supportEmail,
    websiteUrl: settings.websiteUrl ?? '#',
  };
}

/** Every `{{token}}` referenced in a string, de-duplicated and in first-seen order. */
export function extractTokens(html: string): string[] {
  const seen = new Set<string>();
  const re = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    if (m[1] !== undefined) seen.add(m[1]);
  }
  return [...seen];
}

/** Substitute known tokens; unknown tokens are left visible so the operator spots gaps. */
export function renderPreview(html: string, vars: Readonly<Record<string, string>>): string {
  return html.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] ?? whole : whole,
  );
}
